// Messages.js
import React, { useEffect, useState, useRef } from 'react';
import API from './api';
import { io } from 'socket.io-client';

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

const Messages = ({ onRefreshUnread, mode = 'teacher' }) => {
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const [showNewModal, setShowNewModal] = useState(false);
  const [newDirectRecipientId, setNewDirectRecipientId] = useState('');
  const [broadcastMode, setBroadcastMode] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastRecipients, setBroadcastRecipients] = useState([]);
  const [creatingChannel, setCreatingChannel] = useState(false);

  // “all teachers” u broadcast modu
  const [broadcastAllTeachers, setBroadcastAllTeachers] = useState(false);

  // role iz localStorage + mode iz propa
  const rawRole = (localStorage.getItem('role') || '').toLowerCase(); // 'teacher', 'admin', 'super_admin', 'parent'...
  const isAdmin =
    mode === 'admin' ||
    rawRole === 'admin' ||
    rawRole === 'super_admin';

  const isTeacher = rawRole === 'teacher';
  const isParent = rawRole === 'parent';

  const messagesEndRef = useRef(null);

  // Socket.io ref + ref za trenutno selektovani kanal
  const socketRef = useRef(null);
  const selectedChannelIdRef = useRef(null);

  // typing indikator
  const [isSomeoneTyping, setIsSomeoneTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const triggerUnreadRefresh = () => {
    if (typeof onRefreshUnread === 'function') {
      onRefreshUnread();
    }
  };

  // Drži current selectedChannelId u ref-u da bi ga handler iz soketa uvek znao
  useEffect(() => {
    selectedChannelIdRef.current = selectedChannelId;
  }, [selectedChannelId]);

  // ------------------------------------------------
  // API helpers
  // ------------------------------------------------
  const fetchChannels = async () => {
    setLoadingChannels(true);
    try {
      const res = await API.get('/messages/channels');
      setChannels(res.data || []);
    } catch (e) {
      console.error('Failed to load channels', e);
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchMessages = async (channelId) => {
    if (!channelId) return;
    setLoadingMessages(true);
    try {
      const res = await API.get(`/messages/channels/${channelId}`);
      setMessages(res.data || []);
    } catch (e) {
      console.error('Failed to load messages', e);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadRecipients = async () => {
    setRecipientsLoading(true);
    try {
      const res = await API.get('/messages/recipients');
      setRecipients(res.data || []);
    } catch (e) {
      console.error('Failed to load recipients', e);
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  };

  // ------------------------------------------------
  // Socket.IO konekcija + event handleri
  // ------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem('token');

    const s = io('/', {
      path: '/socket.io',
      auth: { token },
    });

    socketRef.current = s;

    const handleNewMessage = async (payload) => {
      if (!payload || !payload.channel_id) return;
      const currentChannelId = selectedChannelIdRef.current;

      // ako gledamo baš taj kanal – povuci poruke (tu BE update-uje last_read_at)
      if (currentChannelId && payload.channel_id === currentChannelId) {
        await fetchMessages(payload.channel_id);
      }

      // uvek osveži listu kanala (new badge + poslednja poruka)
      await fetchChannels();
      triggerUnreadRefresh();
    };

    const handleTyping = (payload) => {
      if (!payload || !payload.channel_id) return;
      const currentChannelId = selectedChannelIdRef.current;
      if (!currentChannelId || payload.channel_id !== currentChannelId) return;

      setIsSomeoneTyping(!!payload.is_typing);
    };

    s.on('connect', () => {
      // console.log('Socket connected', s.id);
    });

    s.on('connect_error', (err) => {
      console.error('Socket connect error:', err.message);
    });

    s.on('new_message', handleNewMessage);
    s.on('typing', handleTyping);

    return () => {
      s.off('new_message', handleNewMessage);
      s.off('typing', handleTyping);
      s.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // jednom pri mount-u

  // ------------------------------------------------
  // Inicijalno učitavanje kanala
  // ------------------------------------------------
  useEffect(() => {
    fetchChannels();
  }, []);

  // kad god se lista kanala promeni, pridruži se S V I M kanalima
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;
    channels.forEach((ch) => {
      if (ch && ch.id) {
        s.emit('join_channel', { channelId: ch.id });
      }
    });
  }, [channels]);

  // ------------------------------------------------
  // Kad se promeni selektovani kanal – povuci poruke
  // ------------------------------------------------
  useEffect(() => {
    const s = socketRef.current;

    if (selectedChannelId && s) {
      s.emit('join_channel', { channelId: selectedChannelId });

      // Učitavanje poruka = mark as read u BE, pa odmah refresh kanala
      fetchMessages(selectedChannelId).then(() => {
        fetchChannels();
        triggerUnreadRefresh();
      });
    }

    return () => {
      const s2 = socketRef.current;
      if (s2 && selectedChannelId) {
        s2.emit('leave_channel', { channelId: selectedChannelId });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId]);

  // Sinkronizacija selectedChannel sa channels
  useEffect(() => {
    if (!channels || channels.length === 0) {
      setSelectedChannel(null);
      return;
    }

    if (!selectedChannelId) {
      // više NE biramo automatski prvi kanal,
      // čekamo da korisnik klikne
      setSelectedChannel(null);
      return;
    }

    const found = channels.find((c) => c.id === selectedChannelId);
    if (found) {
      setSelectedChannel(found);
    } else {
      // ako je kanal obrisan – resetuj selekciju
      setSelectedChannelId(null);
      setSelectedChannel(null);
    }
  }, [channels, selectedChannelId]);

  // auto-scroll na dno kada se promene poruke
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  // cleanup typing timeout pri unmount-u
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // ------------------------------------------------
  // UI helpers
  // ------------------------------------------------

  const handleSelectChannel = (ch) => {
    setSelectedChannelId(ch.id);
    setSelectedChannel(ch);
  };

  const hasUnread = (ch) => {
    if (!ch.last_message_at) return false;
    if (!ch.last_read_at) return true;
    return ch.last_message_at > ch.last_read_at;
  };

  const channelLabel = (ch) => {
    if (ch.type === 'direct') {
      return ch.direct_other_name || 'Direct chat';
    }
    if (ch.type === 'broadcast') {
      return ch.title || 'Announcement';
    }
    if (ch.type === 'group') {
      return ch.title || 'Group chat';
    }
    return ch.title || `Channel #${ch.id}`;
  };

  const handleSendMessage = async () => {
    const body = (newMessage || '').trim();
    if (!body || !selectedChannelId) return;
    setSending(true);
    try {
      await API.post(`/messages/channels/${selectedChannelId}/messages`, {
        body,
      });
      setNewMessage('');

      // pošalji da više ne kuca
      const s = socketRef.current;
      if (s) {
        s.emit('typing', {
          channelId: selectedChannelId,
          isTyping: false,
        });
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // fallback: osveži poruke + kanale
      await fetchMessages(selectedChannelId);
      await fetchChannels();
      triggerUnreadRefresh();
    } catch (e) {
      console.error('Failed to send message', e);
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleNewConversationClick = async () => {
    setShowNewModal(true);
    if (recipients.length === 0) {
      await loadRecipients();
    }
  };

  // --------- GRUPE za recipients (Teachers / Admins / Parents) ----------
  const teacherRecipients = recipients.filter(
    (r) => (r.role || '').toLowerCase() === 'teacher'
  );
  const adminRecipients = recipients.filter(
    (r) => (r.role || '').toLowerCase() === 'admin'
  );
  const parentRecipients = recipients.filter(
    (r) => (r.role || '').toLowerCase() === 'parent'
  );
  const teacherIds = teacherRecipients.map((r) => r.id);

  const toggleBroadcastRecipient = (id) => {
    setBroadcastRecipients((prev) => {
      let next;
      if (prev.includes(id)) {
        next = prev.filter((x) => x !== id);
      } else {
        next = [...prev, id];
      }

      // update “all teachers” flag
      if (teacherIds.length > 0) {
        const allSelected = teacherIds.every((tid) => next.includes(tid));
        setBroadcastAllTeachers(allSelected);
      }

      return next;
    });
  };

  const resetNewChannelState = () => {
    setNewDirectRecipientId('');
    setBroadcastMode(false);
    setBroadcastTitle('');
    setBroadcastBody('');
    setBroadcastRecipients([]);
    setBroadcastAllTeachers(false);
    setCreatingChannel(false);
  };

  const handleCreateChannel = async () => {
    try {
      setCreatingChannel(true);
      if (isAdmin && broadcastMode) {
        // broadcast
        const body = (broadcastBody || '').trim();
        if (!broadcastTitle.trim() || !body || broadcastRecipients.length === 0) {
          alert('Please fill title, message and select at least one recipient.');
          setCreatingChannel(false);
          return;
        }
        const res = await API.post('/messages/broadcast', {
          title: broadcastTitle.trim(),
          body,
          recipients: broadcastRecipients,
        });
        const channelId = res.data.channel_id;
        setShowNewModal(false);
        resetNewChannelState();
        await fetchChannels();
        setSelectedChannelId(channelId);
        await fetchMessages(channelId);
        triggerUnreadRefresh();
      } else {
        // direct
        const otherId = Number(newDirectRecipientId);
        if (!otherId) {
          alert('Please select recipient.');
          setCreatingChannel(false);
          return;
        }
        const res = await API.post('/messages/direct', {
          other_user_id: otherId,
        });
        const channelId = res.data.channel_id;
        const firstMessage = (broadcastBody || '').trim();
        if (firstMessage) {
          await API.post(`/messages/channels/${channelId}/messages`, {
            body: firstMessage,
          });
        }
        setShowNewModal(false);
        resetNewChannelState();
        await fetchChannels();
        setSelectedChannelId(channelId);
        await fetchMessages(channelId);
        triggerUnreadRefresh();
      }
    } catch (e) {
      console.error('Failed to create channel', e);
      alert('Failed to create conversation.');
    } finally {
      setCreatingChannel(false);
    }
  };

  const canUseBroadcast = isAdmin;

  // handler za promenu teksta (typing)
  const handleChangeMessage = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    const s = socketRef.current;
    if (!s || !selectedChannelId) return;

    // čim nešto upišemo – pošalji isTyping: true
    s.emit('typing', {
      channelId: selectedChannelId,
      isTyping: true,
    });

    // resetuj timer – kad prođe 2s bez kucanja, šaljemo isTyping: false
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      const s2 = socketRef.current;
      if (s2 && selectedChannelIdRef.current) {
        s2.emit('typing', {
          channelId: selectedChannelIdRef.current,
          isTyping: false,
        });
      }
      typingTimeoutRef.current = null;
    }, 2000);
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* glavni container */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row bg-white rounded-lg shadow overflow-hidden">
        {/* LEFT: lista kanala */}
        <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r flex flex-col md:min-w-[220px] min-h-0">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
            <span className="text-sm font-semibold">Conversations</span>
            <button
              onClick={handleNewConversationClick}
              className="text-xs bg-purple-600 text-white px-2 py-1 rounded"
            >
              New
            </button>
          </div>

          {loadingChannels ? (
            <div className="flex-1 min-h-0 flex items-center justify-center text-gray-400 text-sm">
              Loading...
            </div>
          ) : channels.length === 0 ? (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-gray-400 text-sm px-3 text-center">
              <p>No conversations yet.</p>
              <p className="mt-1">Click &ldquo;New&rdquo; to start one.</p>
            </div>
          ) : (
            // MOBILE: limit height of conversations list + scroll
            <div className="flex-1 min-h-0 overflow-y-auto max-h-20 md:max-h-none">
              {channels.map((ch) => {
                const isActive = ch.id === selectedChannelId;
                const unread = hasUnread(ch);
                return (
                  <button
                    key={ch.id}
                    onClick={() => handleSelectChannel(ch)}
                    className={classNames(
                      'w-full text-left px-3 py-2 border-b flex flex-col gap-0.5 hover:bg-gray-50',
                      isActive ? 'bg-purple-50' : ''
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-900 truncate">
                        {channelLabel(ch)}
                      </span>
                      {unread && (
                        <span className="inline-flex items-center justify-center text-[10px] px-2 py-0.5 rounded-full bg-purple-600 text-white">
                          new
                        </span>
                      )}
                    </div>
                    {ch.last_message_body && (
                      <div className="text-[11px] text-gray-500 truncate">
                        {ch.last_message_sender_name
                          ? `${ch.last_message_sender_name}: `
                          : ''}
                        {ch.last_message_body}
                      </div>
                    )}
					{ch.last_message_at && (
					  <div className="text-[10px] text-gray-400 mt-0.5">
					    {String(ch.last_message_at).slice(0, 16)}
					  </div>
					)}
                    
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT: poruke */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-semibold truncate">
                {selectedChannel ? channelLabel(selectedChannel) : 'Conversation'}
              </span>
              {selectedChannel && selectedChannel.related_student_name && (
                <span className="text-[11px] text-gray-500 truncate">
                  About: {selectedChannel.related_student_name}
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 bg-gray-50">
            {loadingMessages ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Loading messages...
              </div>
            ) : !selectedChannelId ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm text-center px-4">
                Select a conversation from the list on the left or start a new one.
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm text-center px-4">
                No messages yet. Start the conversation by sending a message below.
              </div>
            ) : (
              <div className="space-y-2">
                {messages.map((msg) => {
                  const isMine = msg.is_mine; // BE daje is_mine na /messages/channels/:id
                  return (
                    <div
                      key={msg.id}
                      className={classNames(
                        'flex mb-1',
                        isMine ? 'justify-end' : 'justify-start'
                      )}
                    >
                      <div
                        className={classNames(
                          'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                          isMine
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-900'
                        )}
                      >
                        {!isMine && (
                          <div className="text-[10px] font-semibold mb-0.5 opacity-70">
                            {msg.sender_name}
                          </div>
                        )}
                        <div className="whitespace-pre-wrap break-words">
                          {msg.body}
                        </div>
                        <div className="text-[9px] mt-1 opacity-70 text-right">
                          {msg.created_at.slice(0, 16).replace('T', ' ')}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* typing indikator */}
          {selectedChannelId && isSomeoneTyping && (
            <div className="px-4 py-1 text-xs text-gray-500 italic bg-gray-50">
              Someone is typing…
            </div>
          )}

          {/* Input zona */}
          <div className="border-t bg-white px-3 py-2">
            <div className="flex items-end gap-2">
              <textarea
                className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none max-h-40"
                rows={2}
                placeholder={
                  selectedChannelId
                    ? 'Type your message...'
                    : 'Select or create a conversation first...'
                }
                value={newMessage}
                onChange={handleChangeMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!sending && selectedChannelId) {
                      handleSendMessage();
                    }
                  }
                }}
                disabled={!selectedChannelId || sending}
              />
              <button
                onClick={handleSendMessage}
                disabled={!selectedChannelId || sending || !newMessage.trim()}
                className={classNames(
                  'px-4 py-2 rounded-lg text-sm font-semibold',
                  (!selectedChannelId || sending || !newMessage.trim())
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                )}
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* NEW CONVERSATION MODAL */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-bold">
                {canUseBroadcast ? 'New Conversation / Announcement' : 'New Conversation'}
              </h3>
              <button
                className="text-gray-500 text-lg"
                onClick={() => {
                  setShowNewModal(false);
                  resetNewChannelState();
                }}
              >
                ✕
              </button>
            </div>

            {recipientsLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                Loading recipients...
              </div>
            ) : recipients.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                No available recipients.
              </div>
            ) : (
              <>
                {canUseBroadcast && (
                  <div className="mb-3">
                    <label className="inline-flex items-center text-xs gap-2">
                      <input
                        type="checkbox"
                        checked={broadcastMode}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBroadcastMode(checked);
                          if (!checked) {
                            setBroadcastAllTeachers(false);
                            setBroadcastRecipients([]);
                          }
                        }}
                      />
                      <span>Send as broadcast / announcement</span>
                    </label>
                  </div>
                )}

                {/* DIRECT MODE */}
                {!broadcastMode && (
                  <>
                    <div className="mb-3">
                      <label className="block text-xs mb-1">To</label>
                      <select
                        className="border rounded px-3 py-2 w-full text-sm"
                        value={newDirectRecipientId}
                        onChange={(e) => setNewDirectRecipientId(e.target.value)}
                      >
                        <option value="">-- Select recipient --</option>

                        {teacherRecipients.length > 0 && (
                          <optgroup label="Teachers">
                            {teacherRecipients.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} {r.email ? `(${r.email})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}

                        {/* Teachers & Admin & Parents – ali:
                            - parent ulogu ograničavamo da mu BE vrati samo default teachere,
                            - teacher/admin mogu da vide i roditelje, pa ih posebno grupišemo. */}
                        {parentRecipients.length > 0 && !isParent && (
                          <optgroup label="Parents">
                            {parentRecipients.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} {r.email ? `(${r.email})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}

                        {adminRecipients.length > 0 && (
                          <optgroup label="Admins">
                            {adminRecipients.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} {r.email ? `(${r.email})` : ''} (ADMIN)
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>

                      {/* NOVO (ostaje isto): checkbox ispod dropdowna – pretvara u broadcast to all teachers */}
                      {canUseBroadcast && teacherRecipients.length > 0 && (
                        <label className="inline-flex items-center gap-2 text-xs mt-2">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                // prebacujemo u broadcast mode sa svim teacherima
                                setBroadcastMode(true);
                                setBroadcastAllTeachers(true);
                                setBroadcastRecipients([...teacherIds]);
                                setNewDirectRecipientId('');
                              }
                            }}
                          />
                          <span>Send this as announcement to all teachers</span>
                        </label>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-xs mb-1">
                        First message (optional)
                      </label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm"
                        rows={3}
                        value={broadcastBody}
                        onChange={(e) => setBroadcastBody(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* BROADCAST MODE */}
                {broadcastMode && (
                  <>
                    <div className="mb-3">
                      <label className="block text-xs mb-1">Announcement title</label>
                      <input
                        type="text"
                        className="border rounded px-3 py-2 w-full text-sm"
                        value={broadcastTitle}
                        onChange={(e) => setBroadcastTitle(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-xs mb-1">Message</label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm"
                        rows={4}
                        value={broadcastBody}
                        onChange={(e) => setBroadcastBody(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs mb-1">Recipients</label>
                      <div className="border rounded px-2 py-2 max-h-40 overflow-y-auto text-sm">
                        {teacherRecipients.length > 0 && (
                          <>
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-[11px] font-semibold text-gray-500">
                                Teachers
                              </div>
                              {/* ALL TEACHERS CHECKBOX */}
                              <label className="inline-flex items-center gap-1 text-[10px] text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={broadcastAllTeachers}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBroadcastAllTeachers(checked);
                                    setBroadcastRecipients((prev) => {
                                      const nonTeacher = prev.filter(
                                        (id) => !teacherIds.includes(id)
                                      );
                                      if (checked) {
                                        return [...nonTeacher, ...teacherIds];
                                      }
                                      return nonTeacher;
                                    });
                                  }}
                                />
                                <span>Select all teachers</span>
                              </label>
                            </div>

                            {teacherRecipients.map((r) => {
                              const checked = broadcastRecipients.includes(r.id);
                              return (
                                <label
                                  key={r.id}
                                  className="flex items-center gap-2 text-xs mb-1"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      toggleBroadcastRecipient(r.id)
                                    }
                                  />
                                  <span>
                                    {r.name} {r.email ? `(${r.email})` : ''}
                                  </span>
                                </label>
                              );
                            })}
                          </>
                        )}

                        {adminRecipients.length > 0 && (
                          <>
                            <div className="mt-2 text-[11px] font-semibold text-gray-500 mb-1">
                              Admins
                            </div>
                            {adminRecipients.map((r) => {
                              const checked = broadcastRecipients.includes(r.id);
                              return (
                                <label
                                  key={r.id}
                                  className="flex items-center gap-2 text-xs mb-1"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      toggleBroadcastRecipient(r.id)
                                    }
                                  />
                                  <span className="inline-flex items-center gap-1">
                                    <span>
                                      {r.name} {r.email ? `(${r.email})` : ''}
                                    </span>
                                    <span className="px-1 py-[1px] rounded-full text-[9px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                      ADMIN
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setShowNewModal(false);
                      resetNewChannelState();
                    }}
                    className="px-4 py-2 text-sm bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateChannel}
                    disabled={creatingChannel}
                    className={classNames(
                      'px-4 py-2 text-sm rounded text-white',
                      creatingChannel
                        ? 'bg-gray-400'
                        : 'bg-purple-600 hover:bg-purple-700'
                    )}
                  >
                    {creatingChannel ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;



